import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { BIZ } from "../lib/constants";

/**
 * Accessible breadcrumb trail + matching JSON-LD.
 * Usage: <Breadcrumbs items={[{label:"Spirits", to:"/spirits"}, {label:"Bourbon"}]} />
 * "Home" is auto-prepended; the last item is treated as current (no link).
 *
 * For full SEO benefit, render BOTH the visual <nav> and the JSON-LD via Helmet —
 * this component renders the visual; consumers should call buildBreadcrumbLd().
 */
export const buildBreadcrumbLd = (items = []) => {
    const full = [{ label: "Home", to: "/" }, ...items];
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: full.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.label,
            ...(it.to && i < full.length - 1 ? { item: `${BIZ.siteUrl}${it.to}` } : {}),
        })),
    };
};

export const Breadcrumbs = ({ items = [] }) => {
    const full = [{ label: "Home", to: "/" }, ...items];
    return (
        <nav
            aria-label="Breadcrumb"
            className="ls-container"
            style={{ paddingTop: 110, paddingBottom: 6 }}
            data-testid="breadcrumbs"
        >
            <ol
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    color: "var(--ls-text-muted)",
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                }}
            >
                {full.map((it, i) => {
                    const last = i === full.length - 1;
                    return (
                        <li key={`${it.label}-${i}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {i > 0 && <ChevronRight size={11} aria-hidden="true" style={{ color: "var(--ls-border-strong)" }} />}
                            {last || !it.to ? (
                                <span aria-current={last ? "page" : undefined} style={{ color: "var(--ls-text-secondary)" }}>
                                    {it.label}
                                </span>
                            ) : (
                                <Link to={it.to} className="link-underline" style={{ color: "var(--ls-text-muted)" }}>
                                    {it.label}
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumbs;
